import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';
import type { QuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links.type';
import { Icon as MuiIcon } from '../../../../ui-library/icon/icon';
import { Tooltip as UiTooltip } from '../../../../ui-library/tooltip/tooltip';
import {
  getTrendColor,
  getTrendDirection,
} from '../../../work-periods/comparison-view/helpers/comparison-table.helpers';
import { getUnitsLabel } from '../../../work-periods/comparison-view/helpers/measure-comparison-chart.helpers';
import { getDatasetColor } from '../artifact-colors';
import { useIsExporting } from '../artifact-export-context';
import type { ArtifactComponentProps } from '../artifact-registry';
import {
  TREND_ICON_MAP,
  getDesiredDirection,
  getInitialChartType,
  getInitialSelectedMeasure,
  getMeasureLabel,
  getMeasureUnits,
  isGroupedComparisonPayload,
  isMeasureComparisonPayload,
  isMultiObjectPayload,
} from './measure-comparison-artifact.helpers';
import type {
  Dataset,
  GroupedComparisonPayload,
  MeasureComparisonPayload,
  MultiObjectPayload,
} from './measure-comparison-artifact.type';
import { ChartType } from './measure-comparison-artifact.type';

/* ── Component ─────────────────────────────────────────── */

/**
 * Renders the Flora chat artifact view for backend-provided measure-comparison
 * data. Dispatches to grouped, multi-object, or single-object sub-charts based
 * on payload shape. `initialState` (when provided by the export route) is
 * forwarded to whichever sub-chart is rendered so screenshot output is
 * deterministic — see `ArtifactComponentProps` JSDoc for the recognized keys.
 *
 * @param props - Standard Flora artifact component props.
 * @returns Measure-comparison chart, table, or grouped subgraphs.
 */
export function MeasureComparisonArtifact({ data, config, quickLinks, initialState }: ArtifactComponentProps) {
  // Detect grouped comparison mode first (multi-board work periods)
  const groupedPayload = useMemo(() => {
    if (isGroupedComparisonPayload(data)) return data as unknown as GroupedComparisonPayload;
    return null;
  }, [data]);

  // Detect multi-object mode
  const multiPayload = useMemo(() => {
    if (groupedPayload) return null;
    if (isMultiObjectPayload(data)) return data as unknown as MultiObjectPayload;
    return null;
  }, [data, groupedPayload]);

  const payload = useMemo(() => {
    if (groupedPayload || multiPayload) return null;
    if (isMeasureComparisonPayload(data)) return data as unknown as MeasureComparisonPayload;
    return null;
  }, [data, groupedPayload, multiPayload]);

  if (groupedPayload) {
    return (
      <GroupedComparisonChart
        data={groupedPayload}
        config={config}
        quickLinks={quickLinks}
        initialState={initialState}
      />
    );
  }

  if (multiPayload) {
    return <MultiObjectChart data={multiPayload} config={config} initialState={initialState} />;
  }

  if (!payload) {
    return <EmptyState>No measure data available</EmptyState>;
  }

  return <SingleObjectChart payload={payload} config={config} initialState={initialState} />;
}

/* ── Single-object / comparison mode (existing behavior) ─ */

/**
 * Renders a single measure-comparison payload (line / bar / table toggle,
 * dual-axis, legend, tooltip). Exported so other artifacts (e.g. measures
 * over time) can reuse the exact same chart body, the way
 * `GroupedComparisonChart` already does internally.
 *
 * @param props.payload - The `MeasureComparisonPayload` to render.
 * @param props.config - Optional artifact config (default_chart_type, etc.).
 * @param props.initialState - Optional export-route seed (chartType, etc.).
 * @returns The chart/table view for one payload.
 */
export function SingleObjectChart({
  payload,
  config,
  initialState,
}: {
  payload: MeasureComparisonPayload;
  config?: Record<string, unknown>;
  initialState?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const isExporting = useIsExporting();

  const defaultChartType = useMemo<ChartType>(() => {
    const seeded = getInitialChartType(initialState);
    if (seeded != null) return seeded;
    const cfgDefault = (config?.default_chart_type as string) ?? '';
    if (cfgDefault === ChartType.Line || cfgDefault === ChartType.Bar || cfgDefault === ChartType.Table) {
      return cfgDefault;
    }
    return payload.scope === 'comparison' ? ChartType.Bar : ChartType.Line;
  }, [config, payload, initialState]);

  const [chartType, setChartType] = useState<ChartType>(defaultChartType);
  const [visibleDatasets, setVisibleDatasets] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(payload.datasets.map((ds) => [ds.key, true])),
  );

  const toggleDataset = useCallback((key: string) => {
    setVisibleDatasets((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const chartData = useMemo(
    () =>
      payload.labels.map((label, labelIdx) => {
        const row: Record<string, string | number | null> = { label };
        for (const ds of payload.datasets) {
          row[ds.key] = ds.data[labelIdx] ?? null;
        }
        return row;
      }),
    [payload],
  );

  const visibleDs = useMemo(
    () => payload.datasets.filter((ds) => visibleDatasets[ds.key] !== false),
    [payload, visibleDatasets],
  );

  // Axis config: split percentage vs non-percentage datasets
  const { hasPercentage, hasNonPercentage, nonPctLabel } = useMemo(() => {
    const pct = payload.datasets.some((ds) => ds.units === 'Percentage');
    const nonPct = payload.datasets.filter((ds) => ds.units !== 'Percentage');
    // Derive a label from the first non-percentage unit (e.g. "Tasks", "Days")
    const uniqueUnits = [...new Set(nonPct.map((ds) => ds.units).filter(Boolean))];
    const label = uniqueUnits.length === 1 ? getUnitsLabel(uniqueUnits[0]!, t) : '';
    return { hasPercentage: pct, hasNonPercentage: nonPct.length > 0, nonPctLabel: label };
  }, [payload.datasets, t]);

  const getAxisId = useCallback((ds: Dataset) => (ds.units === 'Percentage' ? 'pct' : 'left'), []);

  if (payload.datasets.length === 0) {
    return <EmptyState>No measure data available</EmptyState>;
  }

  return (
    <ChartContainer>
      {/* ── Header row ── */}
      <HeaderRow>
        <ChartTypeToggle>
          <ToggleBtn
            $active={chartType === ChartType.Line}
            onClick={() => setChartType(ChartType.Line)}
            aria-label="Line chart"
          >
            <Icon icon="material-symbols:show-chart" width={16} height={16} />
          </ToggleBtn>
          <ToggleBtn
            $active={chartType === ChartType.Bar}
            onClick={() => setChartType(ChartType.Bar)}
            aria-label="Bar chart"
          >
            <Icon icon="material-symbols:bar-chart" width={16} height={16} />
          </ToggleBtn>
          <ToggleBtn
            $active={chartType === ChartType.Table}
            onClick={() => setChartType(ChartType.Table)}
            aria-label="Table view"
          >
            <Icon icon="material-symbols:table-rows" width={16} height={16} />
          </ToggleBtn>
        </ChartTypeToggle>
      </HeaderRow>

      {/* ── Chart / Table ── */}
      {chartType === ChartType.Table ? (
        <TableScroll>
          <StyledTable>
            {payload.scope === 'comparison' ? (
              <>
                {/* Comparison: columns = work periods, rows = measures */}
                <thead>
                  <tr>
                    <Th>Measure</Th>
                    {payload.labels.map((label) => (
                      <Th key={label}>{label}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payload.datasets.map((ds, rowIdx) => {
                    const color = getDatasetColor(rowIdx);
                    const desiredDir = getDesiredDirection(ds.key, payload.measure_details);
                    const unitLabel = ds.units ? getUnitsLabel(ds.units, t) : '';
                    return (
                      <Tr key={ds.key} $striped={rowIdx % 2 === 1}>
                        <Td>
                          <ThContent>
                            <ColorDot style={{ backgroundColor: color }} />
                            {ds.label}
                          </ThContent>
                        </Td>
                        {ds.data.map((val, colIdx) => {
                          const prevVal = colIdx > 0 ? ds.data[colIdx - 1] : null;
                          return (
                            <Td key={payload.labels[colIdx]}>
                              <TdContent>
                                <span>
                                  {val != null ? val : '—'}
                                  {val != null && unitLabel ? <UnitLabel>{unitLabel}</UnitLabel> : null}
                                </span>
                                {val != null && prevVal != null && (
                                  <MuiIcon
                                    name={TREND_ICON_MAP[getTrendDirection(val, prevVal)]}
                                    size={14}
                                    color={getTrendColor(val, prevVal, desiredDir)}
                                  />
                                )}
                              </TdContent>
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </tbody>
              </>
            ) : (
              <>
                {/* Time-series: columns = measures, rows = dates */}
                <thead>
                  <tr>
                    <Th>Date</Th>
                    {payload.datasets.map((ds, idx) => (
                      <Th key={ds.key}>
                        <ThContent>
                          <ColorDot style={{ backgroundColor: getDatasetColor(idx) }} />
                          {ds.label}
                        </ThContent>
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payload.labels.map((label, rowIdx) => (
                    <Tr key={label} $striped={rowIdx % 2 === 1}>
                      <Td>{label}</Td>
                      {payload.datasets.map((ds) => {
                        const val = ds.data[rowIdx];
                        const prevVal = rowIdx > 0 ? ds.data[rowIdx - 1] : null;
                        const desiredDir = getDesiredDirection(ds.key, payload.measure_details);
                        const unitLabel = ds.units ? getUnitsLabel(ds.units, t) : '';
                        return (
                          <Td key={ds.key}>
                            <TdContent>
                              <span>
                                {val != null ? val : '—'}
                                {val != null && unitLabel ? <UnitLabel>{unitLabel}</UnitLabel> : null}
                              </span>
                              {val != null && prevVal != null && (
                                <MuiIcon
                                  name={TREND_ICON_MAP[getTrendDirection(val, prevVal)]}
                                  size={14}
                                  color={getTrendColor(val, prevVal, desiredDir)}
                                />
                              )}
                            </TdContent>
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </tbody>
              </>
            )}
          </StyledTable>
        </TableScroll>
      ) : (
        <div style={{ width: '100%', height: '280px' }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartType === ChartType.Line ? (
              <LineChart data={chartData} margin={{ top: 4, right: hasPercentage ? 8 : 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                {hasNonPercentage && (
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    label={
                      nonPctLabel
                        ? {
                            value: nonPctLabel,
                            angle: -90,
                            position: 'insideLeft',
                            style: { fontSize: 11, fill: 'var(--ink-lighter)' },
                            offset: 10,
                          }
                        : undefined
                    }
                  />
                )}
                {hasPercentage && (
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                )}
                <Tooltip content={(props) => <ChartTooltip {...props} datasets={payload.datasets} />} />
                {visibleDs.map((ds) => {
                  const idx = payload.datasets.indexOf(ds);
                  const color = getDatasetColor(idx);
                  return (
                    <Line
                      key={ds.key}
                      yAxisId={getAxisId(ds)}
                      type="monotone"
                      dataKey={ds.key}
                      name={ds.label}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: color }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                      isAnimationActive={!isExporting}
                    />
                  );
                })}
              </LineChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 4, right: hasPercentage ? 8 : 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                {hasNonPercentage && (
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    label={
                      nonPctLabel
                        ? {
                            value: nonPctLabel,
                            angle: -90,
                            position: 'insideLeft',
                            style: { fontSize: 11, fill: 'var(--ink-lighter)' },
                            offset: 10,
                          }
                        : undefined
                    }
                  />
                )}
                {hasPercentage && (
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                )}
                <Tooltip content={(props) => <ChartTooltip {...props} datasets={payload.datasets} />} />
                {visibleDs.map((ds) => {
                  const idx = payload.datasets.indexOf(ds);
                  const color = getDatasetColor(idx);
                  return (
                    <Bar
                      key={ds.key}
                      yAxisId={getAxisId(ds)}
                      dataKey={ds.key}
                      name={ds.label}
                      fill={color}
                      radius={[3, 3, 0, 0]}
                      isAnimationActive={!isExporting}
                    />
                  );
                })}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Legend (hidden in table view) ── */}
      {chartType !== ChartType.Table && (
        <LegendWrap>
          {payload.datasets.map((ds, idx) => {
            const color = getDatasetColor(idx);
            const checked = visibleDatasets[ds.key] !== false;
            return (
              <LegendItem key={ds.key} onClick={() => toggleDataset(ds.key)} role="button" tabIndex={0}>
                <LegendCheckbox
                  $checked={checked}
                  style={{ borderColor: color, backgroundColor: checked ? color : 'transparent' }}
                >
                  {checked && <Icon icon="material-symbols:check" width={10} height={10} />}
                </LegendCheckbox>
                <LegendLabel $dimmed={!checked}>{ds.label}</LegendLabel>
              </LegendItem>
            );
          })}
        </LegendWrap>
      )}
    </ChartContainer>
  );
}

/* ── Grouped comparison mode (multi-board) ────────────── */

function GroupedComparisonChart({
  data,
  config,
  quickLinks,
  initialState,
}: {
  data: GroupedComparisonPayload;
  config?: Record<string, unknown>;
  quickLinks?: QuickLink[];
  initialState?: Record<string, unknown>;
}) {
  const quickLinksByKey = useMemo(() => {
    if (!quickLinks) return new Map<string, QuickLink>();
    return new Map(quickLinks.filter((link) => link.key).map((link) => [link.key!, link]));
  }, [quickLinks]);

  const handleQuickLinkClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  if (data.groups.length === 0) {
    return <EmptyState>No measure data available</EmptyState>;
  }

  return (
    <ChartContainer>
      {data.groups.map((group, groupIdx) => {
        const groupPayload: MeasureComparisonPayload = {
          scope: 'comparison',
          labels: group.labels,
          datasets: group.datasets,
          measure_details: data.measure_details,
        };

        const groupLink = quickLinksByKey.get(group.group_id);

        return (
          <GroupSection key={group.group_id} $first={groupIdx === 0}>
            <GroupHeaderRow>
              <GroupHeader>{group.group_name}</GroupHeader>
              {groupLink && (
                <UiTooltip variant="black" label={groupLink.label} position="top" withArrow>
                  <GroupQuickLinkButton
                    onClick={() => handleQuickLinkClick(groupLink.url)}
                    aria-label={groupLink.label}
                  >
                    <Icon icon="material-symbols:open-in-new" width={14} height={14} />
                  </GroupQuickLinkButton>
                </UiTooltip>
              )}
            </GroupHeaderRow>
            <SingleObjectChart payload={groupPayload} config={config} initialState={initialState} />
          </GroupSection>
        );
      })}
    </ChartContainer>
  );
}

/* ── Multi-object mode ───────────────────────────────── */

function MultiObjectChart({
  data,
  config,
  initialState,
}: {
  data: MultiObjectPayload;
  config?: Record<string, unknown>;
  initialState?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const defaultChartType = useMemo<ChartType>(() => {
    const seeded = getInitialChartType(initialState);
    if (seeded != null) return seeded;
    const cfgDefault = (config?.default_chart_type as string) ?? '';
    if (cfgDefault === ChartType.Line || cfgDefault === ChartType.Bar || cfgDefault === ChartType.Table) {
      return cfgDefault;
    }
    return ChartType.Line;
  }, [config, initialState]);

  const [chartType, setChartType] = useState<ChartType>(defaultChartType);
  const [selectedMeasure, setSelectedMeasure] = useState<string>(() => {
    const seeded = getInitialSelectedMeasure(initialState);
    if (seeded && (data.available_measures.includes(seeded) || data.measure_details?.some((m) => m.name === seeded))) {
      return seeded;
    }
    return data.available_measures[0] ?? data.measure_details?.[0]?.name ?? '';
  });
  const [visibleObjects, setVisibleObjects] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(data.objects.map((obj) => [obj.id, true])),
  );

  const toggleObject = useCallback((id: string) => {
    setVisibleObjects((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const selectedLabel = useMemo(
    () => getMeasureLabel(selectedMeasure, data.measure_details),
    [selectedMeasure, data.measure_details],
  );

  const selectedUnits = useMemo(
    () => getMeasureUnits(selectedMeasure, data.measure_details),
    [selectedMeasure, data.measure_details],
  );

  // Build Recharts row data: [{label, <objId>: value, ...}]
  const chartData = useMemo(
    () =>
      data.labels.map((label, labelIdx) => {
        const row: Record<string, string | number | null> = { label };
        for (const obj of data.objects) {
          const measureValues = obj.measures[selectedMeasure];
          row[obj.id] = measureValues?.[labelIdx] ?? null;
        }
        return row;
      }),
    [data, selectedMeasure],
  );

  const visibleObjs = useMemo(
    () => data.objects.filter((obj) => visibleObjects[obj.id] !== false),
    [data.objects, visibleObjects],
  );

  // Build pseudo-datasets for the tooltip
  const tooltipDatasets = useMemo<Dataset[]>(
    () =>
      data.objects.map((obj) => ({
        key: obj.id,
        label: obj.name,
        data: obj.measures[selectedMeasure] ?? [],
        units: selectedUnits,
      })),
    [data.objects, selectedMeasure, selectedUnits],
  );

  if (data.objects.length === 0) {
    return <EmptyState>No measure data available</EmptyState>;
  }

  return (
    <ChartContainer>
      {/* ── Header row: measure dropdown + chart toggle ── */}
      <HeaderRow>
        <Menu shadow="sm" radius="md" position="bottom-start" offset={4} withinPortal={false}>
          <Menu.Target>
            <MetricBtn type="button" aria-label="Select measure">
              {selectedLabel}
              <Icon icon="material-symbols:expand-more" width={14} height={14} />
            </MetricBtn>
          </Menu.Target>
          <Menu.Dropdown>
            {data.available_measures.map((m) => (
              <Menu.Item key={m} onClick={() => setSelectedMeasure(m)}>
                {getMeasureLabel(m, data.measure_details)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>

        <ChartTypeToggle>
          <ToggleBtn
            $active={chartType === ChartType.Line}
            onClick={() => setChartType(ChartType.Line)}
            aria-label="Line chart"
          >
            <Icon icon="material-symbols:show-chart" width={16} height={16} />
          </ToggleBtn>
          <ToggleBtn
            $active={chartType === ChartType.Bar}
            onClick={() => setChartType(ChartType.Bar)}
            aria-label="Bar chart"
          >
            <Icon icon="material-symbols:bar-chart" width={16} height={16} />
          </ToggleBtn>
          <ToggleBtn
            $active={chartType === ChartType.Table}
            onClick={() => setChartType(ChartType.Table)}
            aria-label="Table view"
          >
            <Icon icon="material-symbols:table-rows" width={16} height={16} />
          </ToggleBtn>
        </ChartTypeToggle>
      </HeaderRow>

      {/* ── Chart / Table ── */}
      {chartType === ChartType.Table ? (
        <TableScroll>
          <StyledTable>
            <thead>
              <tr>
                <Th>Date</Th>
                {data.objects.map((obj, idx) => (
                  <Th key={obj.id}>
                    <ThContent>
                      <ColorDot style={{ backgroundColor: getDatasetColor(idx) }} />
                      {obj.name}
                    </ThContent>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.labels.map((label, rowIdx) => {
                const desiredDir = getDesiredDirection(selectedMeasure, data.measure_details);
                const unitLabel = selectedUnits ? getUnitsLabel(selectedUnits, t) : '';
                return (
                  <Tr key={label} $striped={rowIdx % 2 === 1}>
                    <Td>{label}</Td>
                    {data.objects.map((obj) => {
                      const val = obj.measures[selectedMeasure]?.[rowIdx] ?? null;
                      const prevVal = rowIdx > 0 ? (obj.measures[selectedMeasure]?.[rowIdx - 1] ?? null) : null;
                      return (
                        <Td key={obj.id}>
                          <TdContent>
                            <span>
                              {val != null ? val : '—'}
                              {val != null && unitLabel ? <UnitLabel>{unitLabel}</UnitLabel> : null}
                            </span>
                            {val != null && prevVal != null && (
                              <MuiIcon
                                name={TREND_ICON_MAP[getTrendDirection(val, prevVal)]}
                                size={14}
                                color={getTrendColor(val, prevVal, desiredDir)}
                              />
                            )}
                          </TdContent>
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableScroll>
      ) : (
        <MultiObjectChartArea
          chartType={chartType}
          chartData={chartData}
          visibleObjs={visibleObjs}
          allObjects={data.objects}
          tooltipDatasets={tooltipDatasets}
          selectedUnits={selectedUnits}
        />
      )}

      {/* ── Legend: objects with checkboxes (hidden in table view) ── */}
      {chartType !== ChartType.Table && (
        <LegendWrap>
          {data.objects.map((obj, idx) => {
            const color = getDatasetColor(idx);
            const checked = visibleObjects[obj.id] !== false;
            return (
              <LegendItem key={obj.id} onClick={() => toggleObject(obj.id)} role="button" tabIndex={0}>
                <LegendCheckbox
                  $checked={checked}
                  style={{ borderColor: color, backgroundColor: checked ? color : 'transparent' }}
                >
                  {checked && <Icon icon="material-symbols:check" width={10} height={10} />}
                </LegendCheckbox>
                <LegendLabel $dimmed={!checked}>{obj.name}</LegendLabel>
              </LegendItem>
            );
          })}
        </LegendWrap>
      )}
    </ChartContainer>
  );
}

/* ── Multi-object chart area (extracted for axis logic) ── */

function MultiObjectChartArea({
  chartType,
  chartData,
  visibleObjs,
  allObjects,
  tooltipDatasets,
  selectedUnits,
}: {
  chartType: ChartType.Line | ChartType.Bar;
  chartData: Record<string, string | number | null>[];
  visibleObjs: MultiObjectPayload['objects'];
  allObjects: MultiObjectPayload['objects'];
  tooltipDatasets: Dataset[];
  selectedUnits: string;
}) {
  const { t } = useTranslation();
  const isExporting = useIsExporting();
  const isPercentage = selectedUnits === 'Percentage';
  const unitLabel = selectedUnits ? getUnitsLabel(selectedUnits, t) : '';

  const yAxisProps = isPercentage
    ? { domain: [0, 100] as [number, number], tickFormatter: (v: number) => `${v}%` }
    : {};

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <ResponsiveContainer width="100%" height="100%">
        {chartType === ChartType.Line ? (
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              label={
                unitLabel && !isPercentage
                  ? {
                      value: unitLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 11, fill: 'var(--ink-lighter)' },
                      offset: 10,
                    }
                  : undefined
              }
              {...yAxisProps}
            />
            <Tooltip content={(props) => <ChartTooltip {...props} datasets={tooltipDatasets} />} />
            {visibleObjs.map((obj) => {
              const idx = allObjects.indexOf(obj);
              const color = getDatasetColor(idx);
              return (
                <Line
                  key={obj.id}
                  type="monotone"
                  dataKey={obj.id}
                  name={obj.name}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: color }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={!isExporting}
                />
              );
            })}
          </LineChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              label={
                unitLabel && !isPercentage
                  ? {
                      value: unitLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 11, fill: 'var(--ink-lighter)' },
                      offset: 10,
                    }
                  : undefined
              }
              {...yAxisProps}
            />
            <Tooltip content={(props) => <ChartTooltip {...props} datasets={tooltipDatasets} />} />
            {visibleObjs.map((obj) => {
              const idx = allObjects.indexOf(obj);
              const color = getDatasetColor(idx);
              return (
                <Bar
                  key={obj.id}
                  dataKey={obj.id}
                  name={obj.name}
                  fill={color}
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={!isExporting}
                />
              );
            })}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/* ── Custom Tooltip ────────────────────────────────────── */

function ChartTooltip({
  active,
  payload: entries,
  label,
  datasets,
}: TooltipContentProps<ValueType, NameType> & { datasets: Dataset[] }) {
  if (!active || !entries?.length) return null;

  // Find units from the datasets array
  const getUnits = (dataKey: string | undefined): string => {
    if (!dataKey) return '';
    const ds = datasets.find((d) => d.key === dataKey);
    return ds?.units ?? '';
  };

  return (
    <TooltipContainer>
      <TooltipLabel>{label}</TooltipLabel>
      {entries.map((entry) => {
        const units = getUnits(entry.dataKey as string);
        return (
          <TooltipRow key={entry.name}>
            <TooltipSwatch style={{ backgroundColor: entry.color }} />
            <span>{entry.name}</span>
            <TooltipValue>
              {entry.value != null ? entry.value : '—'}
              {units ? ` ${units}` : ''}
            </TooltipValue>
          </TooltipRow>
        );
      })}
    </TooltipContainer>
  );
}

/* ── Styled components ─────────────────────────────────── */

const ChartContainer = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

const GroupSection = styled.div<{ $first?: boolean }>`
  ${({ $first }) =>
    $first
      ? ''
      : `
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  `}
`;

const GroupHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px 4px;
`;

const GroupHeader = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-dark);
  margin: 0;
`;

const GroupQuickLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ink-lighter);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--sky-light);
    color: var(--secondary-base);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 4px 10px;
`;

const MetricBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: var(--ink-base);
  margin-right: auto;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const ChartTypeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: transparent;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'var(--secondary-base)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ink-base)')};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--secondary-base)' : 'rgba(0, 0, 0, 0.06)')};
  }
`;

/* ── Table styles ── */

const TableScroll = styled.div`
  max-height: 480px;
  overflow: auto;
  border: 1px solid var(--flora-table-border);
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--flora-table-header-bg);
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--ink-dark);
  border-bottom: 1px solid var(--flora-table-border);
  min-width: 80px;
`;

const ThContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ColorDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const UnitLabel = styled.span`
  margin-left: 2px;
  color: var(--ink-lighter);
  font-weight: 400;
  font-size: 11px;
`;

const Tr = styled.tr<{ $striped: boolean }>`
  background: ${({ $striped }) => ($striped ? 'var(--flora-table-stripe-bg)' : 'transparent')};
`;

const Td = styled.td`
  padding: 6px 12px;
  color: var(--ink-base);
  border-bottom: 1px solid var(--flora-table-border);
`;

const TdContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/* ── Legend ── */

const LegendWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 4px 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const LegendCheckbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid;
  flex-shrink: 0;
  color: #fff;
  transition:
    background-color 0.15s,
    border-color 0.15s;
`;

const LegendLabel = styled.span<{ $dimmed: boolean }>`
  font-size: 12px;
  color: var(--ink-base);
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  transition: opacity 0.15s;
`;

/* ── Tooltip ── */

const TooltipContainer = styled.div`
  background: var(--flora-chart-tooltip-bg);
  border: var(--flora-chart-tooltip-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: var(--flora-chart-tooltip-text);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 0;
`;

const TooltipSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const TooltipValue = styled.span`
  margin-left: auto;
  font-weight: 600;
`;
